/**
 * Tool Privacy Registry
 *
 * Tracks which tools make external API calls and what data they send.
 * Used by PrivacyNotice component to display transparent privacy disclosures.
 */

export type PrivacyType = 'client-only' | 'external-api';

export interface ExternalService {
  /** Name of the external service */
  name: string;
  /** Domain or URL of the service */
  url: string;
  /** Description of what data is sent */
  dataSent: string;
  /** Optional privacy note (e.g., "k-anonymity protected") */
  privacyNote?: string;
}

export interface ToolPrivacyInfo {
  slug: string;
  type: PrivacyType;
  externalServices?: ExternalService[];
}

/**
 * Registry of tools that make external API calls.
 * Tools not listed here are assumed to be client-only.
 */
export const TOOL_PRIVACY_REGISTRY: ToolPrivacyInfo[] = [
  // Malware / threat intel
  {
    slug: 'hash-lookup',
    type: 'external-api',
    externalServices: [
      {
        name: 'abuse.ch (MalwareBazaar, ThreatFox, URLhaus)',
        url: 'abuse.ch',
        dataSent: 'The file hash only — never the file itself',
        privacyNote: 'Files are hashed locally in your browser; only the resulting hash is sent to our lookup API, which queries these threat-intel feeds.',
      },
      {
        name: 'VirusTotal (fallback)',
        url: 'virustotal.com',
        dataSent: 'The file hash only, when the abuse.ch feeds return no match',
        privacyNote: 'Hash-only reputation lookup — the file is never uploaded.',
      },
    ],
  },

  // DNS & Network Lookups
  {
    slug: 'dns-lookup',
    type: 'external-api',
    externalServices: [
      {
        name: 'Cloudflare DNS',
        url: 'cloudflare-dns.com',
        dataSent: 'Domain name you enter',
        privacyNote: 'Public DNS query - queries standard DNS records',
      },
    ],
  },
  {
    slug: 'whois-lookup',
    type: 'external-api',
    externalServices: [
      {
        name: 'WHOIS JSON API',
        url: 'whoisjsonapi.com',
        dataSent: 'Domain name you enter',
        privacyNote: 'Public WHOIS data - domain registration info is public record',
      },
      {
        name: 'RDAP (Backup)',
        url: 'rdap.org',
        dataSent: 'Domain name you enter',
        privacyNote: 'Fallback service using RDAP protocol',
      },
    ],
  },
  {
    slug: 'email-auth-validator',
    type: 'external-api',
    externalServices: [
      {
        name: 'Cloudflare DNS',
        url: 'cloudflare-dns.com',
        dataSent: 'Domain name for SPF, DKIM, and DMARC lookups',
        privacyNote: 'Public DNS query - checks email authentication records',
      },
    ],
  },
  {
    slug: 'email-deliverability-checker',
    type: 'external-api',
    externalServices: [
      {
        name: 'Cloudflare DNS',
        url: 'cloudflare-dns.com',
        dataSent: 'Domain name (and optional DKIM selector) for MX, SPF, DKIM, DMARC, BIMI, MTA-STS and reverse DNS lookups',
        privacyNote: 'Public DNS queries only - no mail is sent and no mail server is contacted',
      },
    ],
  },

  // IP Geolocation & Risk
  {
    slug: 'ip-geolocation-lookup',
    type: 'external-api',
    externalServices: [
      {
        name: 'IP-API',
        url: 'ip-api.com',
        dataSent: 'IP address you enter',
        privacyNote: 'Returns approximate geographic location based on IP',
      },
      {
        name: 'FreeIPAPI (Backup)',
        url: 'freeipapi.com',
        dataSent: 'IP address you enter',
        privacyNote: 'Fallback geolocation service',
      },
    ],
  },
  {
    slug: 'ip-risk-checker',
    type: 'external-api',
    externalServices: [
      {
        name: 'Fraudlogix (via proxy)',
        url: 'api.fraudlogix.com',
        dataSent: 'IP address you enter',
        privacyNote: 'IP reputation and risk scoring service',
      },
      {
        name: 'ipify',
        url: 'api.ipify.org',
        dataSent: 'Auto-detection of your IP address',
        privacyNote: 'Only used when you click "Check My IP"',
      },
    ],
  },

  // Security & Certificate Tools
  {
    slug: 'domain-risk-scanner',
    type: 'external-api',
    externalServices: [
      {
        name: 'InventiveHQ Scan API',
        url: 'inventivehq.com/api',
        dataSent: 'Domain name you enter',
        privacyNote: 'Your domain is sent to our server-side API which performs all security checks. We do not log or store your queries.',
      },
    ],
  },
  {
    slug: 'breach-checker',
    type: 'external-api',
    externalServices: [
      {
        name: 'Have I Been Pwned',
        url: 'api.pwnedpasswords.com',
        dataSent: 'First 5 characters of password SHA-1 hash only',
        privacyNote: 'Uses k-anonymity - your full password is NEVER sent. Only a partial hash prefix is transmitted.',
      },
    ],
  },
  {
    slug: 'certificate-transparency-lookup',
    type: 'external-api',
    externalServices: [
      {
        name: 'crt.sh',
        url: 'crt.sh',
        dataSent: 'Domain name you enter',
        privacyNote: 'Public Certificate Transparency logs - certificates are public record',
      },
    ],
  },
  {
    slug: 'ssl-checker',
    type: 'external-api',
    externalServices: [
      {
        name: 'Target Server',
        url: 'Your specified domain',
        dataSent: 'Domain name you enter',
        privacyNote: 'Connects to the target server to retrieve its SSL certificate',
      },
    ],
  },
  {
    slug: 'security-headers-analyzer',
    type: 'external-api',
    externalServices: [
      {
        name: 'Target URL',
        url: 'Your specified URL',
        dataSent: 'URL you enter',
        privacyNote: 'Fetches HTTP headers from the specified URL',
      },
    ],
  },
  {
    slug: 'ocsp-crl-checker',
    type: 'external-api',
    externalServices: [
      {
        name: 'OCSP Responders',
        url: 'Certificate Authority OCSP endpoints',
        dataSent: 'Certificate serial number and issuer',
        privacyNote: 'Checks certificate revocation status with issuing CA',
      },
    ],
  },

  // CVE & Threat Intel
  {
    slug: 'cve-lookup',
    type: 'external-api',
    externalServices: [
      {
        name: 'CISA KEV Catalog',
        url: 'cisa.gov',
        dataSent: 'Search queries and CVE IDs',
        privacyNote: 'Public vulnerability database - no personal data sent',
      },
    ],
  },
  {
    slug: 'cwe-lookup',
    type: 'external-api',
    externalServices: [
      {
        name: 'MITRE CWE',
        url: 'cwe.mitre.org',
        dataSent: 'CWE ID lookups',
        privacyNote: 'Public weakness enumeration database',
      },
    ],
  },

  // URL & Web Tools
  {
    slug: 'bulk-url-check',
    type: 'external-api',
    externalServices: [
      {
        name: 'Target URLs',
        url: 'URLs you provide',
        dataSent: 'List of URLs to check',
        privacyNote: 'Checks HTTP status codes of provided URLs',
      },
    ],
  },
  {
    slug: 'redirect-chain-checker',
    type: 'external-api',
    externalServices: [
      {
        name: 'Target URL',
        url: 'URL you provide',
        dataSent: 'URL to trace',
        privacyNote: 'Follows and records HTTP redirects',
      },
    ],
  },
  {
    slug: 'robots-txt-analyzer',
    type: 'external-api',
    externalServices: [
      {
        name: 'Target Domain',
        url: 'Domain you specify',
        dataSent: 'Domain name',
        privacyNote: 'Fetches public robots.txt file from the domain',
      },
    ],
  },

  // Network Tools
  {
    slug: 'network-latency-calculator',
    type: 'external-api',
    externalServices: [
      {
        name: 'OpenStreetMap Nominatim',
        url: 'nominatim.openstreetmap.org',
        dataSent: 'City or address names for geocoding',
        privacyNote: 'Converts location names to coordinates for distance calculation',
      },
    ],
  },
  {
    slug: 'mac-address-lookup',
    type: 'external-api',
    externalServices: [
      {
        name: 'MAC Vendor Lookup',
        url: 'api.macvendors.com',
        dataSent: 'MAC address OUI (first 6 characters)',
        privacyNote: 'Identifies hardware manufacturer - public registry data',
      },
    ],
  },
];

/**
 * Get privacy info for a specific tool
 */
export function getToolPrivacyInfo(slug: string): ToolPrivacyInfo | null {
  return TOOL_PRIVACY_REGISTRY.find((t) => t.slug === slug) || null;
}

/**
 * Check if a tool makes external API calls
 */
export function toolHasExternalAPIs(slug: string): boolean {
  const info = getToolPrivacyInfo(slug);
  return info?.type === 'external-api';
}

/**
 * Get all tools that make external API calls
 */
export function getExternalAPITools(): ToolPrivacyInfo[] {
  return TOOL_PRIVACY_REGISTRY.filter((t) => t.type === 'external-api');
}
